import React, { useEffect, useMemo, useCallback, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from '@reach/router';
import clsx from 'clsx';
import qs from 'qs';
import {
  Container,
  Grid,
  makeStyles,
  Theme,
  Tab,
  Breadcrumbs,
  Typography,
} from '@material-ui/core';
import { useAsset, useInit, useLibrary, useScript } from 'hooks';
import { Resolution, SearchCondition, style } from 'commons/constants/KeyFlowConstants';
import { NOT_READY_TO_VIDEO_ERROR } from 'commons/constants/KeyFlowMessage';
import KeyFlowError from 'commons/framework/KeyFlowError';
import AssetVO from 'commons/vo/AssetVO';
import ScriptParamVO from 'commons/vo/param/ScriptParamVO';
import TimeCode from 'commons/utils/TimeCode';
import LibraryParamVO from 'commons/vo/param/LibraryParamVO';
import { AssetState, CodeState, RootState } from 'redux/reducers/type';
import { PreviewParamProps } from 'pages/type';
import useError from 'hooks/useError';
// Component
import { TeamView } from 'components/Team';
import { TabPanel, Tabs } from 'components/Tabs';
import { VideoType } from 'components/VideoPlayer/VideoPlayer';
import { VideoPlayer } from 'components/VideoPlayer';
import { ScoreHeader } from 'components/Score';
import { ScriptList } from 'components/Script';
import { QuarterList } from 'components/Quarter';
import { ScriptOption } from 'components/ScriptOption';
import { InputPartial } from 'components/InputPartial';
import { InputPartialRefProps } from 'components/InputPartial/type';

import CodeVO from 'commons/vo/CodeVO';
import useStatisticsLog from 'hooks/useStatisticsLog';
import StatisticsLogVO from 'commons/vo/param/StatisticsLogVO';
import { Auth } from 'commons/constants/KeyFlowConstants';

import { format, parse } from 'date-fns';
import { formattedDateString } from 'commons/utils/commonUtil';
import { getDayOfWeek } from 'commons/utils/DateUtils';

const useStyles = makeStyles((theme: Theme) => ({
  // 1480px로 breakpoint 설정
  root: {
    padding: '0 20px',
    backgroundColor: style.PREVIEW_BACKGROUND_COLOR,
    maxWidth: '2240px', // 2200px (mainContentWrapper) + 40px (padding 양쪽)
    margin: '0 auto',
    width: '100%',
  },

  mainContentWrapper: {
    width: '100%',
    maxWidth: '2200px',
    margin: '0 auto',
    display: 'flex',
    flexWrap: 'nowrap',
    [theme.breakpoints.down(1480)]: {
      flexDirection: 'column',
    },
  },

  leftContainer: {
    padding: '0 10px 0 0',
    backgroundClip: 'content-box',
    backgroundColor: style.PREVIEW_CONTENTS_BACKGROUND_COLOR,
    flex: '1 1 0',
    minWidth: 0,
    maxWidth: 'none',
    [theme.breakpoints.down(1480)]: {
      padding: '0',
      backgroundColor: 'transparent',
      flexBasis: '100%',
      width: '100%',
    },
  },

  rightContainer: {
    padding: '0 0 0 10px',
    backgroundColor: style.PREVIEW_CONTENTS_BACKGROUND_COLOR,
    flexBasis: '600px',
    width: '600px',
    flexShrink: 0,
    flexGrow: 0,
    [theme.breakpoints.down(1480)]: {
      padding: '0',
      flexBasis: '100%',
      width: '100%',
      flexShrink: 1,
    },
  },
  tabContainer: {},
  tabHeader: {
    border: '0px soild #fff',
  },
  tabIndicator: {
    border: '0px solid tomato',
  },
  partialContainer: {
    padding: '0 20px',
    borderTop: `1px solid ${style.SCRIPT_DIVIDER_BORDER}`,
    borderBottom: `1px solid ${style.SCRIPT_DIVIDER_BORDER}`,
    [theme.breakpoints.down(1480)]: {
      padding: '0',
    },
  },
  quarterContainer: {
    padding: '20px 20px',
    [theme.breakpoints.down(1480)]: {
      padding: '20px 0',
    },
  },
  teamInfoContainer: {
    padding: '20px 15px',
    [theme.breakpoints.down(1480)]: {
      padding: '20px 0',
    },
  },
  teamInfo: {
    padding: '5px',
    [theme.breakpoints.down(1480)]: {
      padding: '0',
    },
  },
  headerBreadcrumbsContainer: {
    height: 'auto',
    padding: '20px 0px',
  },
  headerBreadcrumbs: {
    color: '#999',
    fontSize: '14px',
    fontWeight: 'bold',
  },
  streamingCount: {
    color: '#fff',
    marginLeft: '20px',
    marginTop: '16px',
    fontSize: '14px',
  },
}));

/*
 * Header breadCrumbs 생성
 */
interface HeaderBreadCrumbsProps {
  asset: any;
}

function HeaderBreadCrumbs(props: HeaderBreadCrumbsProps) {
  const classes = useStyles();

  const { asset } = props;

  const { wkblSeasonList, wkblGameTypeList } = useSelector((state: RootState) => state.code);

  let season, gameType, title;

  if (asset && asset.data) {
    const seasonId = asset.data.SEASON;
    const gameTypeId = asset.data.GAME_TYPE;

    const seasonIndex = wkblSeasonList.findIndex((item: any) => item.id === seasonId);
    const gameTypeIndex = wkblGameTypeList.findIndex((item: any) => item.id === gameTypeId);

    season = seasonIndex !== -1 && wkblSeasonList[seasonIndex].name;
    gameType = gameTypeIndex !== -1 && wkblGameTypeList[gameTypeIndex].name;
    title = asset.title;
  }

  return (
    <Grid
      container
      className={classes.headerBreadcrumbsContainer}
      direction="row"
      justifyContent="center"
      alignItems="center"
    >
      <Grid item xs={12}>
        {season && gameType && title && (
          <Breadcrumbs className={classes.headerBreadcrumbs} separator=">">
            <Typography className={classes.headerBreadcrumbs} color="textPrimary">
              {season}
            </Typography>
            <Typography className={classes.headerBreadcrumbs} color="textPrimary">
              {gameType}
            </Typography>
            <Typography className={classes.headerBreadcrumbs} color="textPrimary">
              {title}
            </Typography>
          </Breadcrumbs>
        )}
      </Grid>
    </Grid>
  );
}

/*
 * 팀 정보 생성
 */

interface TeamInfoProps {
  asset: AssetVO | null;
  team1: any;
  team2: any;
  onClickPerson: (name: string) => void;
}

function findPerson(personIdList: Array<string>, list: Array<any>, teamName: string) {
  const personList: Array<any> = [];
  personIdList?.forEach((personId: any) => {
    const personIndex = list?.findIndex((item: any) => item.ID === Number(personId));

    if (personIndex !== -1) {
      const person = list[personIndex];

      personList.push({
        id: person.ID,
        personId: person.PERSON_ID,
        teamName,
        number: person.UNIFORM_NUMBER,
        name: person.NAME,
      });
    }
  });

  // 짝수로 맞춰주기
  if (personList.length % 2 !== 0) {
    personList.push({
      id: personList.length,
      personId: null,
      teamName: null,
      number: null,
      name: null,
    });
  }

  return personList;
}

function TeamInfo(info: TeamInfoProps) {
  const classes = useStyles();

  const { asset, team1, team2, onClickPerson } = info;

  // team1 -> 홈팀
  // team2 -> 어웨이팀
  if (asset && team1 && team2) {
    const { data } = asset;

    const team1CoachId = data.TEAM_1_HEAD_COACH;
    const team1CoachIndex = team1.personList?.findIndex((item: any) => item.ID === team1CoachId);

    const team1CoachName = team1CoachIndex >= 0 ? team1.personList[team1CoachIndex].NAME : '';

    const team2CoachId = data.TEAM_2_HEAD_COACH;
    const team2CoachIndex = team2.personList?.findIndex((item: any) => item.ID === team2CoachId);

    const team2CoachName = team2CoachIndex >= 0 ? team2.personList?.[team2CoachIndex].NAME : '';

    const team1Info = {
      teamName: team1.name,
      teamCoach: team1CoachName,
      teamColor: team1.teamColor,
      personList: findPerson(data?.TEAM_1_PLAYERS?.split(','), team1?.personList, team1.name),
      onClickPerson,
    };

    const team2Info = {
      teamName: team2.name,
      teamCoach: team2CoachName,
      teamColor: team2.teamColor,
      personList: findPerson(data?.TEAM_2_PLAYERS?.split(','), team2.personList, team2.name),
      onClickPerson,
    };

    return (
      <Grid container className={classes.teamInfoContainer}>
        {/* [team 2] 홈 팀 */}
        <Grid item xs={12} lg={6} className={classes.teamInfo}>
          <TeamView {...team1Info} />
        </Grid>
        {/* [team 1] 어웨이 팀 */}
        <Grid item xs={12} lg={6} className={classes.teamInfo}>
          <TeamView {...team2Info} />
        </Grid>
      </Grid>
    );
  }
  return <div></div>;
}

function Preview() {
  const classes = useStyles();
  const queryParams = useParams();
  const params: PreviewParamProps = (() => {
    return {
      assetId: queryParams.assetId && Number(queryParams.assetId),
      in: queryParams.in && Number(queryParams.in),
      out: queryParams.in && Number(queryParams.out),
      keyword: queryParams.keyword,
      subType: queryParams.subType,
    };
  })();
  const location = useLocation();
  const { onRequestAsset, onSelectQuarter } = useAsset();
  const { onRequestScriptList } = useScript();
  const { onInit, isInit } = useInit();
  const { onInsertLibrary } = useLibrary();
  const { onThrowError } = useError();
  const { onRequestStatisticsLog } = useStatisticsLog();

  const { inPoint, outPoint, inTime, outTime, fps } = useSelector(
    (state: RootState) => state.video,
  );

  const { selectedAsset, selectedTeam1, selectedTeam2 } = useSelector(
    (state: RootState): AssetState => state.asset,
  );

  const { isLoading: isLoadingStreamingCount, streamingCount } = useSelector(
    (state: RootState) => state.statisticsLog,
  );

  const { kfScriptRefRangeList } = useSelector(({ code }: RootState): CodeState => code);

  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [isAutoScroll, setIsAutoScroll] = useState<boolean>(false);
  const [currentStreamingCount, setCurrentStreamingCount] = useState<number>(0);

  const videoPlayerRef = useRef<VideoType | null>(null);
  const scriptListRef = useRef<{ scrollTop: (currentTime: number) => void } | null>(null);
  const scriptOptionRef = useRef<{
    onClickPerson: (personName: string) => void;
    onClickSearchKeyword: (keyword: string) => void;
  } | null>(null);
  const inputPartialRef = useRef<InputPartialRefProps>(null);

  // const date = selectedAsset && parse(selectedAsset.dateOfCreated, 'yyyyMMddHHmmss', new Date());
  // UTC +9 시간차이로 기존에 로직과 비슷하게 변경
  const date = selectedAsset && formattedDateString(selectedAsset.gameDate)?.split(' ')[0];

  const { inTimecodeUtil, outTimecodeUtil, durationTimecodeUtil } = useMemo(() => {
    return {
      inTimecodeUtil: new TimeCode(fps),
      outTimecodeUtil: new TimeCode(fps),
      durationTimecodeUtil: new TimeCode(fps),
    };
  }, [fps]);

  const initParams = useMemo(() => {
    return location.search ? qs.parse(location.search, { ignoreQueryPrefix: true }) : null;
  }, [location]);

  const initKeyword: string | undefined = useMemo(() => {
    if (initParams && initParams.keyword) {
      return initParams.keyword as string;
    } else if (initParams && initParams.subType) {
      const code: CodeVO = kfScriptRefRangeList.find(
        (code: CodeVO) => code.codevalue === initParams.subType,
      );
      return code ? code.name : '';
    }
    return undefined;
  }, [initParams, kfScriptRefRangeList]);

  const initInputPartial = useMemo(() => {
    if (initParams && initParams.keyword) {
      return initParams.keyword as string;
    } else if (initParams && initParams.subType) {
      const code: CodeVO = kfScriptRefRangeList.find(
        (code: CodeVO) => code.codevalue === initParams.subType,
      );
      return code ? code.name : '';
    }
    return undefined;
  }, [initParams, kfScriptRefRangeList]);

  const initInTime: number | undefined = useMemo(() => {
    if (initParams && initParams.in) {
      return Number(initParams.in);
    }
    return undefined;
  }, [initParams]);

  const initOutTime: number | undefined = useMemo(() => {
    if (initParams && initParams.out) {
      return Number(initParams.out);
    }
    return undefined;
  }, [initParams]);

  useEffect(() => {
    if (!isLoadingStreamingCount && streamingCount) {
      setCurrentStreamingCount(streamingCount);
    }
  }, [streamingCount, isLoadingStreamingCount]);

  useEffect(() => {
    if (isInit) {
      onRequestAsset({ ...params, ...initParams });
    }
  }, [isInit, initParams]);

  useEffect(() => {
    /**
     * TODO
     *  <ScrollToTop/> 컴포넌트 렌더링으로 왜 안되는 이유 확인 필요
     */
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const user = localStorage.getItem(Auth.USER_ID);
    const param = new StatisticsLogVO({
      data: [
        {
          ASSET_ID: params.assetId,
          TASK_TYPE: 'STREAMING',
        },
      ],
      user,
    });

    onRequestStatisticsLog(param);
  }, []);

  const onSelectTab = useCallback((event: React.ChangeEvent<{}>, newValue: number) => {
    setSelectedTab(newValue);
  }, []);

  const onInsertLowresPartial = useCallback(
    (partialTitle: string, isClean?: boolean) => {
      const { categoryId, id, title, lowresFileName } = selectedAsset as AssetVO;
      inTimecodeUtil.setSeconds(inTime);
      outTimecodeUtil.setSeconds(outTime);
      durationTimecodeUtil.setSeconds(outTime - inTime);

      if (videoPlayerRef.current) {
        const param = new LibraryParamVO({
          contents: partialTitle,
          inPoint: inTimecodeUtil.getFrames(),
          outPoint: outTimecodeUtil.getFrames(),
          resolution: isClean ? Resolution.PROXY : Resolution.LOWRES,
          categoryId,
          videoId: id,
          assetTitle: title,
          startTimecode: inTimecodeUtil.getTimeCodeWithFrame(),
          endTimecode: outTimecodeUtil.getTimeCodeWithFrame(),
          duration: durationTimecodeUtil.getSeconds(),
          ...(isClean ? { isClean: true } : { isClean: false }),
        });
        onInsertLibrary(param);
      } else {
        onThrowError(new KeyFlowError(NOT_READY_TO_VIDEO_ERROR));
        return;
      }

      inputPartialRef.current?.onFocusInputPartial();
    },
    [
      inTime,
      outTime,
      inPoint,
      outPoint,
      inTimecodeUtil,
      outTimecodeUtil,
      durationTimecodeUtil,
      selectedAsset,
      onInsertLibrary,
    ],
  );

  const onChangeAutoScroll = useCallback(() => {
    setIsAutoScroll(!isAutoScroll);
  }, [isAutoScroll]);

  const onChangeCurrentTime = useCallback(
    (currentTime: number) => {
      if (videoPlayerRef.current) {
        videoPlayerRef.current.changeVideoCurrentTime(currentTime);
      }
    },
    [videoPlayerRef.current],
  );

  const onChangeInTime = useCallback(
    (inTime: number) => {
      if (videoPlayerRef.current) {
        videoPlayerRef.current.changeVideoInTime(inTime);
      }
    },
    [videoPlayerRef.current],
  );

  const onChangeOutTime = useCallback(
    (outTime: number) => {
      if (videoPlayerRef.current) {
        videoPlayerRef.current.changeVideoOutTime(outTime);
      }
    },
    [videoPlayerRef.current],
  );

  const onChangeScrollHeight = useCallback(
    (currentTime: number) => {
      if (scriptListRef.current) {
        scriptListRef.current.scrollTop(currentTime);
      }
    },
    [scriptListRef.current],
  );

  const onChangePartialTitle = useCallback(
    (title: string) => {
      if (inputPartialRef.current) {
        inputPartialRef.current.onChangePartialTitle(title);
      }
    },
    [inputPartialRef.current],
  );
  const onClickSearchKeyword = useCallback(
    (keyword: string) => {
      if (scriptOptionRef.current) {
        scriptOptionRef.current.onClickSearchKeyword(keyword);
      }
    },
    [scriptOptionRef.current],
  );

  const onClickPerson = useCallback(
    (personName: string) => {
      if (scriptOptionRef.current) {
        scriptOptionRef.current.onClickPerson(personName);
      }
    },
    [scriptOptionRef.current],
  );

  const onClearPartial = useCallback(() => {
    if (videoPlayerRef.current) {
      videoPlayerRef.current.clearPartial();
    }
  }, []);

  const onClickQuarter = useCallback(
    ({
      inTime,
      outTime,
      inPoint,
      outPoint,
      keyword,
    }: {
      inTime: number;
      outTime: number;
      inPoint: number;
      outPoint: number;
      keyword: string;
    }) => {
      if (videoPlayerRef.current) {
        const { assetId } = params;
        videoPlayerRef.current.changeVideoInTime(inTime);
        videoPlayerRef.current.changeVideoOutTime(outTime);
        videoPlayerRef.current.changeVideoCurrentTime(inTime);

        onChangePartialTitle(keyword);
        // 쿼터 선택 시에도 스크립트 명을 이벤트 로그 검색 명에 삽입 할 경우 변경
        // onChangeSearchKeyword(keyword);
      }
    },
    [videoPlayerRef.current, params.assetId, onRequestScriptList, onChangePartialTitle],
  );

  const onClickAllQuarter = useCallback(() => {
    if (videoPlayerRef.current) {
      const { assetId } = params;
      onClearPartial();

      onChangePartialTitle('');
      onSelectQuarter(null);
    }
  }, [
    videoPlayerRef.current,
    params.assetId,
    onRequestScriptList,
    onChangePartialTitle,
    onClearPartial,
    onSelectQuarter,
  ]);

  const team1SearchKeywordList = useMemo(() => {
    if (!selectedAsset?.data?.TEAM_1_PLAYERS) return [];

    if (selectedAsset && selectedTeam1 && selectedTeam1.personList) {
      return findPerson(
        selectedAsset?.data?.TEAM_1_PLAYERS.split(','),
        selectedTeam1?.personList,
        selectedTeam1?.name,
      );
    }

    return [];
  }, [selectedAsset, selectedTeam1]);

  const team2SearchKeywordList = useMemo(() => {
    if (!selectedAsset?.data?.TEAM_2_PLAYERS) return [];

    if (selectedAsset && selectedTeam2 && selectedTeam2.personList) {
      return findPerson(
        selectedAsset?.data?.TEAM_2_PLAYERS.split(','),
        selectedTeam2?.personList,
        selectedTeam2?.name,
      );
    }

    return [];
  }, [selectedAsset, selectedTeam2]);

  const isHideTeamInfo = useMemo(() => {
    // 두 팀 정보가 모두 있을 때만 표시
    return !(selectedTeam1 && selectedTeam2);
  }, [selectedTeam1, selectedTeam2]);
  return (
    <>
      <Container className={classes.root} component="article" maxWidth={false}>
        <HeaderBreadCrumbs asset={selectedAsset} />
        <div className={classes.mainContentWrapper}>
          <div className={clsx(classes.leftContainer)}>
            {!isHideTeamInfo && <ScoreHeader />}
            <Grid container>
              <Grid item xs={12}>
                <VideoPlayer
                  ref={videoPlayerRef}
                  initInTime={initInTime}
                  initOutTime={initOutTime}
                  onChangeScrollHeight={onChangeScrollHeight}
                />
              </Grid>
              <Grid item xs={12} className={classes.streamingCount}>
                {selectedAsset &&
                  `조회수 ${currentStreamingCount}회 · ${date} (${getDayOfWeek(date)})`}
              </Grid>
              <Grid item xs={12} className={classes.quarterContainer}>
                {selectedAsset && (
                  <QuarterList
                    asset={selectedAsset}
                    onClickQuarter={onClickQuarter}
                    onClickAllQuarter={onClickAllQuarter}
                  />
                )}
              </Grid>
              <Grid item xs={12} className={classes.partialContainer}>
                <InputPartial
                  ref={inputPartialRef}
                  initPartialTitle={initInputPartial}
                  onInsertLowresPartial={onInsertLowresPartial}
                  onChangeInTime={onChangeInTime}
                  onChangeOutTime={onChangeOutTime}
                  onChangeCurrentTime={onChangeCurrentTime}
                  onClearPartial={onClearPartial}
                />
              </Grid>
            </Grid>
            {/* Team Info. */}
            {!isHideTeamInfo && (
              <TeamInfo
                asset={selectedAsset}
                team1={selectedTeam1}
                team2={selectedTeam2}
                onClickPerson={onClickPerson}
              />
            )}
          </div>
          <div className={classes.rightContainer}>
            <div className={clsx(classes.tabContainer)}>
              <Tabs value={selectedTab} onChange={onSelectTab}>
                <Tab label="이벤트 로그" />
              </Tabs>
              <TabPanel value={selectedTab} index={0}>
                <ScriptOption
                  ref={scriptOptionRef}
                  isAutoScroll={isAutoScroll}
                  isHideTeamInfo={isHideTeamInfo}
                  onChangeAutoScroll={onChangeAutoScroll}
                  initKeyword={initKeyword}
                  team1SearchKeywordList={team1SearchKeywordList}
                  team2SearchKeywordList={team2SearchKeywordList}
                />
                <ScriptList
                  ref={scriptListRef}
                  autoScrollMode={isAutoScroll}
                  onChangeCurrentTime={onChangeCurrentTime}
                  onChangeInTime={onChangeInTime}
                  onChangeOutTime={onChangeOutTime}
                  onChangePartialTitle={onChangePartialTitle}
                  team1PlayerList={team1SearchKeywordList}
                  team2PlayerList={team2SearchKeywordList}
                />
              </TabPanel>
            </div>
          </div>
        </div>
      </Container>
    </>
  );
}

export default Preview;
